import React, { useState } from "react";
import { Menu, MenuItem, Tooltip } from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { GenerationType } from "../../../../../../../../utils/consts";

const OPTIONS = Object.values(GenerationType);
// Fallback shift (px) right of the trigger when StorySetup isn't on screen
const MENU_LEFT_OFFSET = 50;

// The menu's right edge lines up with the StorySetup panel's right edge so the
// two read as one column; falls back to an offset from the trigger.
const getMenuPosition = (trigger) => {
  const triggerRect = trigger.getBoundingClientRect();
  const storySetup = document.querySelector(".story-setup-cont");
  const right = storySetup
    ? storySetup.getBoundingClientRect().right
    : triggerRect.right + MENU_LEFT_OFFSET;
  return { top: triggerRect.top, left: right };
};

const GenerationTypeMenu = ({
  value,
  onChange,
  disabled = false,
  lockedMessage = "The generation mode cannot be changed",
  // When set, every option except the current one is disabled with this tooltip.
  otherOptionsDisabledMessage,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const selected =
    OPTIONS.find((option) => option.value === value) || OPTIONS[0];

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setAnchorEl(null);
  };

  return (
    <>
      <Tooltip
        title={disabled ? lockedMessage : ""}
        placement="top"
        arrow
      >
        {/* Locked: label and caret stay, the menu just won't open. `aria-disabled`
            rather than `disabled` so the button still emits the hover events the
            tooltip needs — the click is guarded instead. */}
        <button
          type="button"
          className={`generation-type-trigger ${disabled ? "is-locked" : ""}`}
          aria-disabled={disabled}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={(event) => {
            if (disabled) return;
            setAnchorEl(event.currentTarget);
          }}
        >
          <span className="generation-type-trigger-label">{selected.label}</span>
          <KeyboardArrowUpIcon
            className={`generation-type-trigger-caret ${open ? "open" : ""}`}
          />
        </button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        // Positioned from explicit coords so `left` can track the StorySetup
        // edge; input sits at the bottom of the panel, so it opens upward
        anchorReference="anchorPosition"
        anchorPosition={anchorEl ? getMenuPosition(anchorEl) : undefined}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        // v5 Menu only exposes `root`/`paper` here; list padding goes via MenuListProps
        slotProps={{
          paper: {
            sx: {
              width: 340,
              maxWidth: "calc(100vw - 32px)",
              marginTop: "-8px",
              borderRadius: "10px",
              boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
            },
          },
        }}
        MenuListProps={{ sx: { padding: 0 } }}
      >
        {OPTIONS.map((option) => {
          const isOptionDisabled =
            Boolean(otherOptionsDisabledMessage) &&
            option.value !== selected.value;

          return (
            <Tooltip
              key={option.value}
              title={isOptionDisabled ? otherOptionsDisabledMessage : ""}
              placement="left"
              arrow
            >
              {/* `aria-disabled` + guarded click instead of `disabled`, since a
                  disabled MenuItem drops pointer events and the tooltip with them. */}
              <MenuItem
                selected={option.value === selected.value}
                aria-disabled={isOptionDisabled}
                onClick={() => {
                  if (isOptionDisabled) return;
                  handleSelect(option.value);
                }}
                sx={{
                  alignItems: "center",
                  gap: "12px",
                  padding: "14px 16px",
                  whiteSpace: "normal",
                  borderBottom: "1px solid #ECECEC",
                  "&:last-of-type": { borderBottom: "none" },
                  "&.Mui-selected": { backgroundColor: "#EFF7FF" },
                  "&.Mui-selected:hover": { backgroundColor: "#E4F1FF" },
                  ...(isOptionDisabled && {
                    opacity: 0.5,
                    cursor: "not-allowed",
                    "&:hover": { backgroundColor: "transparent" },
                  }),
                }}
              >
                <div className="generation-type-option">
                  <span className="generation-type-option-title">
                    {option.label}
                  </span>
                  <span className="generation-type-option-description">
                    {option.description}
                  </span>
                </div>
                {option.badge && (
                  <span className="generation-type-option-badge">
                    {option.badge}
                  </span>
                )}
              </MenuItem>
            </Tooltip>
          );
        })}
      </Menu>
    </>
  );
};

export default GenerationTypeMenu;
